// src/components/chemistry/AnalyseView.tsx
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useColourPalette } from '../../context/ColourPaletteContext';
import StatsPanel, { buildSignificanceBrackets } from './StatsPanel';
import {
  descriptiveStats, oneWayAnova, tukeyHSD, dunnsTest,
  significanceStars,
} from '../../utils/statistics';
import { formatAxisLabel } from '../../utils/unitConversion';
import StructureImage from './StructureImage';
import ExportButton from '../export/ExportButton';
import {
  buildMarginalHistogramTraces,
  DEFAULT_SCATTER_POINT_OPACITY,
  discoverImportedColumns,
  partitionRowsBySelection,
  plotlySelectedPoints,
  selectedRowIdsFromPlotlyEvent,
  SCATTER_CATEGORY_COLOURS,
  scatterPointOpacity,
  scatterPointSize,
} from './analyseScatter';

// ── Types ──
interface Molecule {
  id?: number;
  smiles?: string;
  properties?: Record<string, any>;
  [key: string]: any;
}

interface AnalyseViewProps {
  fileId?: number;
  molecules: Molecule[];
  propertyColumns: string[];
  selectedMoleculeIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  onMoleculeClick?: (id: number) => void;
  patentZoneColumn?: string;
  columnUnitsMap?: Record<string, { unit: string | null; unitType: string | null; displayName: string | null }>;
  unitOverrides?: Record<string, { originalUnit: string; displayUnit: string; unitType: string }>;
}

type ResultQualifier = '<' | '<=' | '>' | '>=' | '~' | null;

interface ParsedNumericResult {
  value: number | null;
  qualifier: ResultQualifier;
  raw: any;
}

const parseNumericResult = (raw: any): ParsedNumericResult => {
  if (typeof raw === 'number') {
    return { value: Number.isFinite(raw) ? raw : null, qualifier: null, raw };
  }
  if (raw == null) return { value: null, qualifier: null, raw };
  const text = String(raw).trim();
  if (!text || /^(?:na|n\/a|nd|n\.d\.|not tested|missing|null|—|-)$/i.test(text)) {
    return { value: null, qualifier: null, raw };
  }
  const match = text.match(/^(<=|>=|<|>|~|≈)?\s*([-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)/);
  if (!match) return { value: null, qualifier: null, raw };
  const value = Number(match[2]);
  return {
    value: Number.isFinite(value) ? value : null,
    qualifier: match[1] === '≈' ? '~' : (match[1] as ResultQualifier || null),
    raw,
  };
};

const ZONE_LABELS: Record<number, string> = {
  1: 'Patent literal match',
  2: 'Patent high similarity',
  3: 'Patent moderate similarity',
  4: 'Patent low similarity',
  5: 'No close patent match',
};

const SPOTIFY_GREEN = '#1db954';

// Drug-like reference values (Lipinski-ish) for radar chart
const DRUG_LIKE_REFERENCE: Record<string, number> = {
  mw: 500, MW: 500, 'Molecular Weight': 500, molecular_weight: 500,
  logp: 5, LogP: 5, logP: 5, ALogP: 5, alogp: 5, clogp: 5, CLogP: 5,
  tpsa: 140, TPSA: 140, 'Polar Surface Area': 140,
  hbd: 5, HBD: 5, 'H-Bond Donors': 5, NumHBD: 5,
  hba: 10, HBA: 10, 'H-Bond Acceptors': 10, NumHBA: 10,
  rotatable_bonds: 10, RotBonds: 10, rotbonds: 10, NumRotatableBonds: 10,
};

// ── Theme-aware Plotly layout ──
const getThemeColors = () => {
  const isDark = document.body.classList.contains('theme-dark');
  return isDark ? {
    plotBg: 'rgba(24,24,24,0.6)',
    fontColor: '#b3b3b3',
    gridColor: 'rgba(255,255,255,0.06)',
    zeroLineColor: 'rgba(255,255,255,0.1)',
    tickColor: '#888',
    hoverBg: '#ccfbf1',
    hoverBorder: '#5eead4',
    hoverFont: '#134e4a',
  } : {
    plotBg: 'rgba(255,255,255,0.9)',
    fontColor: '#374151',
    gridColor: 'rgba(0,0,0,0.06)',
    zeroLineColor: 'rgba(0,0,0,0.1)',
    tickColor: 'var(--text-secondary)',
    hoverBg: '#ecfdf5',
    hoverBorder: '#99f6e4',
    hoverFont: '#134e4a',
  };
};

const makeLayout = (overrides: any = {}): any => {
  const t = getThemeColors();
  return {
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: t.plotBg,
    font: { family: "'Inter', -apple-system, sans-serif", color: t.fontColor, size: 11 },
    margin: { t: 8, r: 16, b: 44, l: 54 },
    xaxis: {
      gridcolor: t.gridColor,
      zerolinecolor: t.zeroLineColor,
      tickfont: { size: 10, color: t.tickColor },
      ...overrides.xaxis,
    },
    yaxis: {
      gridcolor: t.gridColor,
      zerolinecolor: t.zeroLineColor,
      tickfont: { size: 10, color: t.tickColor },
      ...overrides.yaxis,
    },
    hoverlabel: {
      bgcolor: t.hoverBg,
      bordercolor: t.hoverBorder,
      font: { family: "'Inter', sans-serif", color: t.hoverFont, size: 12 },
    },
    dragmode: 'select',
    selectdirection: 'any',
    showlegend: false,
    autosize: true,
    ...overrides,
  };
};

const PLOTLY_CONFIG: any = {
  displayModeBar: true,
  displaylogo: false,
  responsive: true,
  scrollZoom: true,
  modeBarButtonsToRemove: ['sendDataToCloud'],
  toImageButtonOptions: {
    format: 'png',
    filename: 'adroit-visualisation',
    scale: 2,
  },
};

// ── Axis Dropdown ──
const AxisDropdown: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  getLabel?: (col: string) => string;
}> = ({ label, value, onChange, options, getLabel }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{
      fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase',
      letterSpacing: '0.08em', fontWeight: 600,
    }}>{label}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '4px',
        color: 'var(--text-primary)', fontSize: '12px', padding: '3px 6px', cursor: 'pointer',
        outline: 'none', fontFamily: 'inherit',
      }}
    >
      {options.map(col => (
        <option key={col} value={col}>{getLabel ? getLabel(col) : col}</option>
      ))}
    </select>
  </div>
);

// ── Mini Toggle Button ──
const MiniToggle: React.FC<{
  options: { key: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => (
  <div style={{
    display: 'flex', background: 'var(--card-bg)', borderRadius: '4px',
    border: '1px solid var(--card-border)', overflow: 'hidden',
  }}>
    {options.map(opt => (
      <button
        key={opt.key}
        onClick={() => onChange(opt.key)}
        style={{
          padding: '2px 7px', border: 'none', fontSize: '10px',
          background: value === opt.key ? 'rgba(29, 185, 84, 0.2)' : 'transparent',
          color: value === opt.key ? SPOTIFY_GREEN : 'var(--text-muted)',
          cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// ── Chart Panel Wrapper ──
const ChartPanel: React.FC<{
  title: string;
  icon: string;
  controls?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, icon, controls, children, style }) => (
  <div style={{
    background: 'var(--card-bg)',
    borderRadius: '8px',
    border: '1px solid var(--card-border)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...style,
  }}>
    <div style={{
      padding: '8px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: '1px solid var(--divider)',
      background: 'var(--hover-bg)',
      minHeight: '36px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <span style={{ fontSize: '12px' }}>{icon}</span>
        <span style={{
          fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)',
          letterSpacing: '0.02em',
        }}>{title}</span>
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {controls}
      </div>
    </div>
    <div style={{ flex: 1, padding: '4px', position: 'relative', minHeight: 0 }}>
      {children}
    </div>
  </div>
);

// ── Layout options ──
type LayoutMode = 'scatter-table' | '2x2' | '3d' | 'parcoords' | 'radar' | 'correlation';

const LAYOUT_OPTIONS: { key: LayoutMode; label: string; icon: string }[] = [
  { key: 'scatter-table', label: 'Scatter + Table', icon: '\u25e7' },
  { key: '2x2', label: '2\u00d72 Grid', icon: '\u229e' },
  { key: '3d', label: '3D Explorer', icon: '\u25c8' },
  { key: 'parcoords', label: 'Parallel Coordinates', icon: '\u2261' },
  { key: 'radar', label: 'Radar', icon: '\u25ce' },
  { key: 'correlation', label: 'Correlation', icon: '\u25a6' },
];

// ── Helper: get a property value from a molecule ──
const getProp = (mol: Molecule, key: string): any => {
  if (key in mol) return (mol as any)[key];
  return mol.properties?.[key];
};

// ── Pearson correlation ──
const pearsonR = (xs: number[], ys: number[]): number => {
  const n = xs.length;
  if (n < 2) return 0;
  let sumX = 0, sumY = 0;
  for (let i = 0; i < n; i++) { sumX += xs[i]; sumY += ys[i]; }
  const meanX = sumX / n, meanY = sumY / n;
  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX, dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const den = Math.sqrt(denX * denY);
  return den === 0 ? 0 : num / den;
};

const rankValues = (values: number[]): number[] => {
  const ordered = values.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value);
  const ranks = new Array(values.length).fill(0);
  for (let i = 0; i < ordered.length;) {
    let j = i + 1;
    while (j < ordered.length && ordered[j].value === ordered[i].value) j += 1;
    const averageRank = (i + j - 1) / 2 + 1;
    for (let k = i; k < j; k += 1) ranks[ordered[k].index] = averageRank;
    i = j;
  }
  return ranks;
};

const spearmanR = (xs: number[], ys: number[]): number => {
  if (xs.length !== ys.length || xs.length < 2) return 0;
  return pearsonR(rankValues(xs), rankValues(ys));
};

const POINT_SYMBOLS = ['circle', 'square', 'diamond', 'cross', 'triangle-up', 'triangle-down', 'star', 'hexagon'];

const numericRange = (values: Array<number | null>, minSize = 7, maxSize = 20): number[] => {
  const valid = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (valid.length === 0) return values.map(() => 9);
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  if (max === min) return values.map(() => (minSize + maxSize) / 2);
  return values.map(value => value == null ? minSize : minSize + ((value - min) / (max - min)) * (maxSize - minSize));
};

const regressionLine = (
  xs: number[],
  ys: number[],
  xLog: boolean,
  yLog: boolean,
): { x: number[]; y: number[]; slope: number; intercept: number; r2: number } | null => {
  const pairs = xs.map((x, i) => ({ x, y: ys[i] }))
    .filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y) && (!xLog || x > 0) && (!yLog || y > 0));
  if (pairs.length < 2) return null;
  const tx = pairs.map(p => xLog ? Math.log10(p.x) : p.x);
  const ty = pairs.map(p => yLog ? Math.log10(p.y) : p.y);
  const meanX = tx.reduce((a, b) => a + b, 0) / tx.length;
  const meanY = ty.reduce((a, b) => a + b, 0) / ty.length;
  const ssX = tx.reduce((sum, x) => sum + (x - meanX) ** 2, 0);
  if (ssX === 0) return null;
  const slope = tx.reduce((sum, x, i) => sum + (x - meanX) * (ty[i] - meanY), 0) / ssX;
  const intercept = meanY - slope * meanX;
  const fitted = tx.map(x => intercept + slope * x);
  const ssTot = ty.reduce((sum, y) => sum + (y - meanY) ** 2, 0);
  const ssRes = ty.reduce((sum, y, i) => sum + (y - fitted[i]) ** 2, 0);
  const ends = [Math.min(...tx), Math.max(...tx)];
  return {
    x: ends.map(x => xLog ? 10 ** x : x),
    y: ends.map(x => {
      const y = intercept + slope * x;
      return yLog ? 10 ** y : y;
    }),
    slope,
    intercept,
    r2: ssTot === 0 ? 1 : 1 - ssRes / ssTot,
  };
};

// ── Main Component ──
const AnalyseView: React.FC<AnalyseViewProps> = ({
  fileId,
  molecules,
  propertyColumns,
  selectedMoleculeIds,
  onSelectionChange,
  onMoleculeClick,
  patentZoneColumn,
  columnUnitsMap,
  unitOverrides,
}) => {
  const { palette } = useColourPalette();

  // Helper to format axis labels with units (respects display unit overrides from grid)
  const getAxisLabel = useCallback((col: string): string => {
    const unitInfo = columnUnitsMap?.[col];
    const override = unitOverrides?.[col];
    const displayUnit = override?.displayUnit || unitInfo?.unit;
    return formatAxisLabel(col, displayUnit);
  }, [columnUnitsMap, unitOverrides]);
  const ZONE_COLORS = palette.zones;
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('2x2');
  const [boxMode, setBoxMode] = useState<'box' | 'violin'>('violin');
  const [showStats, setShowStats] = useState(false);
  const [distMode, setDistMode] = useState<'histogram' | 'bar'>('histogram');

  // Track theme for re-rendering Plotly charts
  const [isDark, setIsDark] = useState(() => document.body.classList.contains('theme-dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.body.classList.contains('theme-dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const [barGroupCol, setBarGroupCol] = useState<string>('zone');

  const importedColumns = useMemo(
    () => discoverImportedColumns(molecules, propertyColumns),
    [molecules, propertyColumns],
  );
  const allDataColumns = importedColumns.all;
  const numericColumns = importedColumns.numeric;
  // Every imported non-numeric data column is available for categorical colouring.
  const categoricalColumns = useMemo(
    () => ['zone', ...importedColumns.categorical],
    [importedColumns.categorical],
  );

  // Default axis selections
  const [scatterX, setScatterX] = useState(numericColumns[0] || 'mw');
  const [scatterY, setScatterY] = useState(numericColumns[1] || 'logp');
  const [histCol, setHistCol] = useState(numericColumns[0] || 'mw');
  const [boxCol, setBoxCol] = useState(numericColumns[1] || 'logp');
  const [scatter3dX, setScatter3dX] = useState(numericColumns[0] || 'mw');
  const [scatter3dY, setScatter3dY] = useState(numericColumns[1] || 'logp');
  const [scatter3dZ, setScatter3dZ] = useState(numericColumns[2] || 'tpsa');
  const [colourBy, setColourBy] = useState<string>('none');
  const [sizeBy, setSizeBy] = useState<string>('none');
  const [shapeBy, setShapeBy] = useState<string>('none');
  const [pointOpacity, setPointOpacity] = useState(DEFAULT_SCATTER_POINT_OPACITY);
  const [showTrendline, setShowTrendline] = useState(false);
  const [scatterXScale, setScatterXScale] = useState<'linear' | 'log'>('linear');
  const [scatterYScale, setScatterYScale] = useState<'linear' | 'log'>('linear');
  const [scatterXMin, setScatterXMin] = useState('');
  const [scatterXMax, setScatterXMax] = useState('');
  const [scatterYMin, setScatterYMin] = useState('');
  const [scatterYMax, setScatterYMax] = useState('');
  const [histogramBins, setHistogramBins] = useState(12);
  const [showIdentityLine, setShowIdentityLine] = useState(false);
  const [showMarginalHistograms, setShowMarginalHistograms] = useState(true);
  const [includeQualifiedInFit, setIncludeQualifiedInFit] = useState(false);
  const [excludedRegressionIds, setExcludedRegressionIds] = useState<Set<number>>(new Set());
  const [showThresholds, setShowThresholds] = useState(false);
  const [showQuadrants, setShowQuadrants] = useState(false);
  const [xThreshold, setXThreshold] = useState('');
  const [yThreshold, setYThreshold] = useState('');
  const [hoveredMoleculeId, setHoveredMoleculeId] = useState<number | null>(null);
  const [savedChartName, setSavedChartName] = useState('');
  const [savedCharts, setSavedCharts] = useState<Array<{ name: string; config: Record<string, any> }>>([]);

  // Update defaults when columns change
  useEffect(() => {
    if (numericColumns.length > 0) {
      setScatterX(prev => numericColumns.includes(prev) ? prev : numericColumns[0]);
      setScatterY(prev => numericColumns.includes(prev) ? prev : numericColumns[Math.min(1, numericColumns.length - 1)]);
      setHistCol(prev => numericColumns.includes(prev) ? prev : numericColumns[0]);
      setBoxCol(prev => numericColumns.includes(prev) ? prev : numericColumns[Math.min(1, numericColumns.length - 1)]);
      setScatter3dX(prev => numericColumns.includes(prev) ? prev : numericColumns[0]);
      setScatter3dY(prev => numericColumns.includes(prev) ? prev : numericColumns[Math.min(1, numericColumns.length - 1)]);
      setScatter3dZ(prev => numericColumns.includes(prev) ? prev : numericColumns[Math.min(2, numericColumns.length - 1)]);
      setColourBy(prev => prev === 'none' || prev === 'zone' || numericColumns.includes(prev) || categoricalColumns.includes(prev) ? prev : 'none');
      setSizeBy(prev => prev === 'none' || numericColumns.includes(prev) ? prev : 'none');
      setShapeBy(prev => prev === 'none' || categoricalColumns.includes(prev) ? prev : 'none');
    }
  }, [numericColumns, categoricalColumns]);

  const handleSelect = useCallback((ids: Set<number>) => {
    onSelectionChange(ids);
  }, [onSelectionChange]);

  const clearSelection = useCallback(() => {
    onSelectionChange(new Set());
  }, [onSelectionChange]);

  const getZone = useCallback((mol: Molecule): number => {
    if (patentZoneColumn) {
      const val = getProp(mol, patentZoneColumn);
      if (val != null) return parseInt(val) || 5;
    }
    return 5;
  }, [patentZoneColumn]);

  const getNumeric = useCallback((mol: Molecule, key: string): number | null => {
    return parseNumericResult(getProp(mol, key)).value;
  }, []);

  const getName = useCallback((mol: Molecule): string => {
    const p = mol.properties || {};
    return p.name || p.compound_name || p.preferred_name || p.Name || p.Compound_Name
      || p.Compound_ID || p.compound_id || p.Internal_Code || p.internal_code || `Mol ${mol.id || '?'}`;
  }, []);

  const getMolId = useCallback((mol: Molecule): number => {
    return mol.id || 0;
  }, []);

  const getCategory = useCallback((mol: Molecule, key: string): string => {
    if (key === 'zone') return ZONE_LABELS[getZone(mol)] || `Zone ${getZone(mol)}`;
    const value = getProp(mol, key);
    return value == null || value === '' ? '(empty)' : String(value);
  }, [getZone]);

  const getAxisRange = useCallback((minText: string, maxText: string, scale: 'linear' | 'log') => {
    if (minText.trim() === '' || maxText.trim() === '') return undefined;
    const min = Number(minText);
    const max = Number(maxText);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return undefined;
    if (scale === 'log') {
      if (min <= 0 || max <= 0) return undefined;
      return [Math.log10(min), Math.log10(max)];
    }
    return [min, max];
  }, []);

  const savedChartStorageKey = `adroit-saved-charts:${fileId || 'all-compounds'}`;

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(savedChartStorageKey);
      setSavedCharts(stored ? JSON.parse(stored) : []);
    } catch {
      setSavedCharts([]);
    }
  }, [savedChartStorageKey]);

  const applySavedChart = useCallback((config: Record<string, any>) => {
    if (config.layoutMode) setLayoutMode(config.layoutMode);
    if (config.scatterX) setScatterX(config.scatterX);
    if (config.scatterY) setScatterY(config.scatterY);
    if (config.colourBy) setColourBy(config.colourBy);
    if (config.sizeBy) setSizeBy(config.sizeBy);
    if (config.shapeBy) setShapeBy(config.shapeBy);
    if (typeof config.pointOpacity === 'number') setPointOpacity(config.pointOpacity);
    if (typeof config.showTrendline === 'boolean') setShowTrendline(config.showTrendline);
    if (typeof config.showIdentityLine === 'boolean') setShowIdentityLine(config.showIdentityLine);
    if (typeof config.showMarginalHistograms === 'boolean') setShowMarginalHistograms(config.showMarginalHistograms);
    if (typeof config.includeQualifiedInFit === 'boolean') setIncludeQualifiedInFit(config.includeQualifiedInFit);
    if (config.scatterXScale) setScatterXScale(config.scatterXScale);
    if (config.scatterYScale) setScatterYScale(config.scatterYScale);
    setScatterXMin(config.scatterXMin || '');
    setScatterXMax(config.scatterXMax || '');
    setScatterYMin(config.scatterYMin || '');
    setScatterYMax(config.scatterYMax || '');
    if (typeof config.showThresholds === 'boolean') setShowThresholds(config.showThresholds);
    if (typeof config.showQuadrants === 'boolean') setShowQuadrants(config.showQuadrants);
    setXThreshold(config.xThreshold || '');
    setYThreshold(config.yThreshold || '');
    if (typeof config.histogramBins === 'number') setHistogramBins(config.histogramBins);
  }, []);

  const saveCurrentChart = useCallback(() => {
    const name = savedChartName.trim() || `Chart ${savedCharts.length + 1}`;
    const config = {
      layoutMode, scatterX, scatterY, colourBy, sizeBy, shapeBy, pointOpacity,
      showTrendline, showIdentityLine, showMarginalHistograms, includeQualifiedInFit,
      scatterXScale, scatterYScale, scatterXMin, scatterXMax, scatterYMin, scatterYMax,
      showThresholds, showQuadrants, xThreshold, yThreshold, histogramBins,
    };
    const next = [...savedCharts.filter(chart => chart.name !== name), { name, config }];
    setSavedCharts(next);
    window.localStorage.setItem(savedChartStorageKey, JSON.stringify(next));
    setSavedChartName(name);
  }, [savedChartName, savedCharts, savedChartStorageKey, layoutMode, scatterX, scatterY, colourBy,
    sizeBy, shapeBy, pointOpacity, showTrendline, showIdentityLine, showMarginalHistograms, includeQualifiedInFit,
    scatterXScale, scatterYScale, scatterXMin, scatterXMax, scatterYMin, scatterYMax,
    showThresholds, showQuadrants, xThreshold, yThreshold, histogramBins]);

  const deleteSavedChart = useCallback(() => {
    if (!savedChartName) return;
    const next = savedCharts.filter(chart => chart.name !== savedChartName);
    setSavedCharts(next);
    window.localStorage.setItem(savedChartStorageKey, JSON.stringify(next));
    setSavedChartName('');
  }, [savedChartName, savedCharts, savedChartStorageKey]);

  // ── Min/Max per numeric column (for radar normalisation) ──
  const columnMinMax = useMemo(() => {
    const mm: Record<string, { min: number; max: number }> = {};
    numericColumns.forEach(col => {
      const vals = molecules.map(m => getNumeric(m, col)).filter((v): v is number => v !== null);
      if (vals.length === 0) { mm[col] = { min: 0, max: 1 }; return; }
      mm[col] = { min: Math.min(...vals), max: Math.max(...vals) };
    });
    return mm;
  }, [molecules, numericColumns, getNumeric]);

  // ── Scatter Plot ──
  const renderScatter = useCallback((xKey: string, yKey: string) => {
    const validSubset = molecules.filter(m => {
      const x = getNumeric(m, xKey);
      const y = getNumeric(m, yKey);
      return x !== null && y !== null && (scatterXScale !== 'log' || x > 0) && (scatterYScale !== 'log' || y > 0);
    });
    const xs = validSubset.map(m => getNumeric(m, xKey) as number);
    const ys = validSubset.map(m => getNumeric(m, yKey) as number);
    const fitSubset = validSubset.filter(m => {
      if (excludedRegressionIds.has(getMolId(m))) return false;
      if (includeQualifiedInFit) return true;
      return !parseNumericResult(getProp(m, xKey)).qualifier && !parseNumericResult(getProp(m, yKey)).qualifier;
    });
    const fitXs = fitSubset.map(m => getNumeric(m, xKey) as number);
    const fitYs = fitSubset.map(m => getNumeric(m, yKey) as number);
    const qualifiedExcluded = includeQualifiedInFit ? 0 : validSubset.filter(m =>
      !excludedRegressionIds.has(getMolId(m)) &&
      Boolean(parseNumericResult(getProp(m, xKey)).qualifier || parseNumericResult(getProp(m, yKey)).qualifier)
    ).length;

    const colourIsNumeric = colourBy !== 'none' && numericColumns.includes(colourBy);
    const colourCategories = colourIsNumeric || colourBy === 'zone' || colourBy === 'none'
      ? []
      : Array.from(new Set(validSubset.map(m => getCategory(m, colourBy))));
    const shapeCategories = shapeBy === 'none'
      ? []
      : Array.from(new Set(validSubset.map(m => getCategory(m, shapeBy))));

    const markerColours = colourBy === 'none'
      ? validSubset.map(() => '#0f9f8f')
      : colourIsNumeric
      ? validSubset.map(m => getNumeric(m, colourBy))
      : colourBy === 'zone'
        ? validSubset.map(m => ZONE_COLORS[getZone(m)])
        : validSubset.map(m => SCATTER_CATEGORY_COLOURS[
            Math.max(0, colourCategories.indexOf(getCategory(m, colourBy))) % SCATTER_CATEGORY_COLOURS.length
          ]);
    const baseSizes = sizeBy === 'none'
      ? validSubset.map(() => 10)
      : numericRange(validSubset.map(m => getNumeric(m, sizeBy)));
    const markerSizes = baseSizes.map((size, i) =>
      scatterPointSize(size, selectedMoleculeIds.has(getMolId(validSubset[i])))
    );
    const markerSymbols = shapeBy === 'none'
      ? validSubset.map(m => {
          const qualifier = parseNumericResult(getProp(m, xKey)).qualifier || parseNumericResult(getProp(m, yKey)).qualifier;
          if (qualifier === '<' || qualifier === '<=') return 'triangle-down';
          if (qualifier === '>' || qualifier === '>=') return 'triangle-up';
          if (qualifier === '~') return 'diamond';
          return 'circle';
        })
      : validSubset.map(m => POINT_SYMBOLS[Math.max(0, shapeCategories.indexOf(getCategory(m, shapeBy))) % POINT_SYMBOLS.length]);

    const pointsTrace: any = {
      x: xs,
      y: ys,
      customdata: validSubset.map(m => getMolId(m)),
      selectedpoints: plotlySelectedPoints(validSubset
        .map((m, index) => selectedMoleculeIds.has(getMolId(m)) ? index : -1)
        .filter(index => index >= 0)),
      selected: { marker: { opacity: 1 } },
      unselected: { marker: { opacity: pointOpacity } },
      text: validSubset.map(m => {
        const details = [
          `<b>${getName(m)}</b>`,
          `${getAxisLabel(xKey)}: ${getProp(m, xKey) ?? '—'}`,
          `${getAxisLabel(yKey)}: ${getProp(m, yKey) ?? '—'}`,
          excludedRegressionIds.has(getMolId(m)) ? '<b>Withheld from regression</b>' : 'Included in chart',
        ];
        if (colourBy !== 'none' && colourBy !== 'zone' && colourBy !== xKey && colourBy !== yKey) details.push(`${colourBy}: ${getProp(m, colourBy) ?? '—'}`);
        if (sizeBy !== 'none' && sizeBy !== xKey && sizeBy !== yKey) details.push(`${sizeBy}: ${getProp(m, sizeBy) ?? '—'}`);
        if (shapeBy !== 'none') details.push(`${shapeBy}: ${getCategory(m, shapeBy)}`);
        return details.join('<br>');
      }),
      hoverinfo: 'text' as const,
      mode: 'markers' as const,
      name: 'Molecules',
      marker: {
        color: markerColours,
        colorscale: colourIsNumeric ? 'Viridis' : undefined,
        showscale: colourIsNumeric,
        colorbar: colourIsNumeric ? { title: { text: getAxisLabel(colourBy), font: { size: 10 } }, thickness: 12 } : undefined,
        size: markerSizes,
        symbol: markerSymbols,
        opacity: validSubset.map(m => scatterPointOpacity(
          pointOpacity,
          selectedMoleculeIds.size > 0,
          selectedMoleculeIds.has(getMolId(m)),
        )),
        line: {
          color: validSubset.map(m => excludedRegressionIds.has(getMolId(m)) ? '#f59e0b' : selectedMoleculeIds.has(getMolId(m)) ? '#111827' : 'rgba(0,0,0,0.45)'),
          width: validSubset.map(m => excludedRegressionIds.has(getMolId(m)) ? 3 : selectedMoleculeIds.has(getMolId(m)) ? 2.5 : 0.75),
        },
      },
      type: 'scatter' as const,
    };

    const traces: any[] = [pointsTrace];
    const xExtent = xs.length ? [Math.min(...xs), Math.max(...xs)] : [0, 1];
    const yExtent = ys.length ? [Math.min(...ys), Math.max(...ys)] : [0, 1];

    if (showIdentityLine && xs.length > 0) {
      const identityMin = Math.max(xExtent[0], yExtent[0], scatterXScale === 'log' || scatterYScale === 'log' ? Number.MIN_VALUE : -Infinity);
      const identityMax = Math.min(xExtent[1], yExtent[1]);
      if (identityMin < identityMax) {
        traces.push({
          x: [identityMin, identityMax], y: [identityMin, identityMax], type: 'scatter', mode: 'lines',
          name: 'y = x', line: { color: '#64748b', width: 1.5, dash: 'dash' },
          hovertemplate: 'Identity line: y = x<extra></extra>',
        });
      }
    }

    let trend: ReturnType<typeof regressionLine> = null;
    if (showTrendline) {
      trend = regressionLine(fitXs, fitYs, scatterXScale === 'log', scatterYScale === 'log');
      if (trend) {
        traces.push({
          x: trend.x,
          y: trend.y,
          type: 'scatter',
          mode: 'lines',
          name: `Linear fit (n=${fitXs.length})`,
          line: { color: '#111827', width: 2 },
          hovertemplate: `Linear fit<br>slope = ${trend.slope.toFixed(4)}<br>intercept = ${trend.intercept.toFixed(4)}<br>R² = ${trend.r2.toFixed(4)}<extra></extra>`,
        });
      }
    }
    if (showMarginalHistograms) {
      traces.push(...buildMarginalHistogramTraces(
        xs, ys, getAxisLabel(xKey), getAxisLabel(yKey), histogramBins,
      ));
    }

    const omitted = molecules.length - validSubset.length;
    const annotations: any[] = [];
    if (omitted > 0 || excludedRegressionIds.size > 0 || qualifiedExcluded > 0) annotations.push({
      x: 0, y: showMarginalHistograms ? 0.81 : 1, xref: 'paper', yref: 'paper', xanchor: 'left', yanchor: 'top',
      text: [
        omitted > 0 ? `${omitted} missing/invalid point${omitted === 1 ? '' : 's'} omitted` : '',
        excludedRegressionIds.size > 0 ? `${excludedRegressionIds.size} point${excludedRegressionIds.size === 1 ? '' : 's'} withheld from fit` : '',
        qualifiedExcluded > 0 && !includeQualifiedInFit ? `${qualifiedExcluded} qualified result${qualifiedExcluded === 1 ? '' : 's'} excluded from fit` : '',
      ].filter(Boolean).join(' · '),
      showarrow: false, font: { size: 10, color: '#f59e0b' }, bgcolor: 'rgba(255,255,255,0.8)', borderpad: 3,
    });
    if (showTrendline && trend) annotations.push({
      x: showMarginalHistograms ? 0.84 : 1, y: showMarginalHistograms ? 0.81 : 1, xref: 'paper', yref: 'paper', xanchor: 'right', yanchor: 'top',
      text: `n=${fitXs.length} · slope=${trend.slope.toFixed(4)} · intercept=${trend.intercept.toFixed(4)} · R²=${trend.r2.toFixed(4)} · Pearson r=${pearsonR(fitXs, fitYs).toFixed(4)} · Spearman ρ=${spearmanR(fitXs, fitYs).toFixed(4)}`,
      showarrow: false, align: 'right', font: { size: 10, color: getThemeColors().fontColor },
      bgcolor: isDark ? 'rgba(24,24,24,0.86)' : 'rgba(255,255,255,0.88)', borderpad: 4,
    });

    const xCut = Number(xThreshold);
    const yCut = Number(yThreshold);
    const validXCut = showThresholds && xThreshold.trim() !== '' && Number.isFinite(xCut) && (scatterXScale !== 'log' || xCut > 0);
    const validYCut = showThresholds && yThreshold.trim() !== '' && Number.isFinite(yCut) && (scatterYScale !== 'log' || yCut > 0);
    const shapes: any[] = [];
    let xThresholdShapeIndex = -1;
    let yThresholdShapeIndex = -1;
    if (validXCut) {
      xThresholdShapeIndex = shapes.length;
      shapes.push({ type: 'line', x0: xCut, x1: xCut, y0: yExtent[0], y1: yExtent[1], xref: 'x', yref: 'y', editable: true, line: { color: '#f59e0b', width: 2, dash: 'dot' } });
    }
    if (validYCut) {
      yThresholdShapeIndex = shapes.length;
      shapes.push({ type: 'line', x0: xExtent[0], x1: xExtent[1], xref: 'x', y0: yCut, y1: yCut, yref: 'y', editable: true, line: { color: '#f59e0b', width: 2, dash: 'dot' } });
    }
    if (showQuadrants && validXCut && validYCut) {
      const quadrantFill = ['rgba(14,165,233,0.05)', 'rgba(139,92,246,0.05)', 'rgba(249,115,22,0.05)', 'rgba(20,184,166,0.05)'];
      shapes.push(
        { type: 'rect', x0: xExtent[0], x1: xCut, y0: yExtent[0], y1: yCut, editable: false, fillcolor: quadrantFill[0], line: { width: 0 }, layer: 'below' },
        { type: 'rect', x0: xCut, x1: xExtent[1], y0: yExtent[0], y1: yCut, editable: false, fillcolor: quadrantFill[1], line: { width: 0 }, layer: 'below' },
        { type: 'rect', x0: xExtent[0], x1: xCut, y0: yCut, y1: yExtent[1], editable: false, fillcolor: quadrantFill[2], line: { width: 0 }, layer: 'below' },
        { type: 'rect', x0: xCut, x1: xExtent[1], y0: yCut, y1: yExtent[1], editable: false, fillcolor: quadrantFill[3], line: { width: 0 }, layer: 'below' },
      );
    }

    const previewId = hoveredMoleculeId ?? Array.from(selectedMoleculeIds)[0] ?? null;
    const previewMolecule = previewId == null ? undefined : molecules.find(m => getMolId(m) === previewId);

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <Plot
          data={traces}
          layout={makeLayout({
          xaxis: {
            ...makeLayout().xaxis,
            domain: showMarginalHistograms ? [0, 0.84] : [0, 1],
            title: { text: getAxisLabel(xKey), font: { size: 11, color: '#888' } },
            type: scatterXScale,
            range: getAxisRange(scatterXMin, scatterXMax, scatterXScale),
          },
          yaxis: {
            ...makeLayout().yaxis,
            domain: showMarginalHistograms ? [0, 0.82] : [0, 1],
            title: { text: getAxisLabel(yKey), font: { size: 11, color: '#888' } },
            type: scatterYScale,
            range: getAxisRange(scatterYMin, scatterYMax, scatterYScale),
          },
          xaxis2: showMarginalHistograms ? {
            domain: [0.87, 1], showgrid: false, zeroline: false, showticklabels: false,
          } : undefined,
          yaxis2: showMarginalHistograms ? {
            domain: [0.85, 1], showgrid: false, zeroline: false, showticklabels: false,
          } : undefined,
          annotations,
          shapes,
          dragmode: 'lasso',
          showlegend: showTrendline || showIdentityLine,
          legend: { orientation: 'h', x: 0, y: 1.08, font: { size: 10 } },
        })}
          config={{ ...PLOTLY_CONFIG, editable: showThresholds, edits: { shapePosition: true } }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
          onHover={(e: any) => setHoveredMoleculeId(e?.points?.[0]?.customdata ?? null)}
          onUnhover={() => setHoveredMoleculeId(null)}
          onRelayout={(event: any) => {
            const shapeList = Array.isArray(event?.shapes) ? event.shapes : null;
            if (xThresholdShapeIndex >= 0) {
              const value = event?.[`shapes[${xThresholdShapeIndex}].x0`] ?? event?.[`shapes[${xThresholdShapeIndex}].x1`] ?? shapeList?.[xThresholdShapeIndex]?.x0;
              if (Number.isFinite(Number(value))) setXThreshold(Number(value).toPrecision(6));
            }
            if (yThresholdShapeIndex >= 0) {
              const value = event?.[`shapes[${yThresholdShapeIndex}].y0`] ?? event?.[`shapes[${yThresholdShapeIndex}].y1`] ?? shapeList?.[yThresholdShapeIndex]?.y0;
              if (Number.isFinite(Number(value))) setYThreshold(Number(value).toPrecision(6));
            }
          }}
          onSelected={(e: any) => {
          if (e && Array.isArray(e.points)) {
            const selectedIds = selectedRowIdsFromPlotlyEvent(e, traces);
            // Plotly may emit an empty selection while React redraws a
            // controlled selectedpoints array. Do not let that transient event
            // erase a valid lasso selection; the explicit selection chip clears it.
            if (selectedIds.size > 0) handleSelect(selectedIds);
          }
        }}
          onClick={(e: any) => {
          if (e && e.points && e.points.length > 0) {
            const id = e.points[0].customdata;
            if (!Number.isFinite(id)) return;
            const newSet = new Set(selectedMoleculeIds);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            handleSelect(newSet);
          }
        }}
        />
        {previewMolecule && (
          <div style={{
            position: 'absolute', left: 58, bottom: 48, width: 128, padding: 6,
            background: isDark ? '#ccfbf1' : '#ecfdf5',
            border: `1px solid ${isDark ? '#5eead4' : '#99f6e4'}`,
            borderRadius: 6, pointerEvents: 'none', zIndex: 5,
            boxShadow: '0 8px 24px rgba(15, 118, 110, 0.14)',
          }}>
            <StructureImage smiles={previewMolecule.smiles} moleculeId={getMolId(previewMolecule)} size={116} />
            <div style={{ fontSize: 10, fontWeight: 600, color: '#134e4a', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getName(previewMolecule)}
            </div>
          </div>
        )}
      </div>
    );
  }, [molecules, selectedMoleculeIds, getZone, getNumeric, getName, getMolId, getCategory, handleSelect,
    isDark, getAxisLabel, numericColumns, colourBy, sizeBy, shapeBy, pointOpacity, showTrendline,
    showIdentityLine, showMarginalHistograms, histogramBins, includeQualifiedInFit, excludedRegressionIds, showThresholds, showQuadrants,
    xThreshold, yThreshold, hoveredMoleculeId, scatterXScale, scatterYScale,
    scatterXMin, scatterXMax, scatterYMin, scatterYMax, getAxisRange]);

  // ── Histogram ──
  const renderHistogram = useCallback((column: string) => {
    const allValues = molecules
      .map(m => getNumeric(m, column))
      .filter((v): v is number => v !== null);

    const selectedValues = molecules
      .filter(m => selectedMoleculeIds.has(getMolId(m)))
      .map(m => getNumeric(m, column))
      .filter((v): v is number => v !== null);

    const traces: any[] = [{
      x: allValues,
      type: 'histogram',
      marker: {
        color: `${SPOTIFY_GREEN}99`,
        line: { color: SPOTIFY_GREEN, width: 1 },
      },
      nbinsx: histogramBins,
      name: 'All',
      hovertemplate: `${column}: %{x}<br>Count: %{y}<extra></extra>`,
    }];

    if (selectedMoleculeIds.size > 0 && selectedValues.length > 0) {
      traces.push({
        x: selectedValues,
        type: 'histogram',
        marker: { color: `${SPOTIFY_GREEN}BB`, line: { color: SPOTIFY_GREEN, width: 1 } },
        nbinsx: histogramBins,
        name: 'Selected',
        hovertemplate: `${column}: %{x}<br>Selected: %{y}<extra></extra>`,
      });
    }

    // Descriptive stats annotation
    const stats = descriptiveStats(allValues);
    const statsAnnotation = stats ? [{
      x: 1, y: 1,
      xref: 'paper' as const, yref: 'paper' as const,
      text: `n=${stats.n}  \u03BC=${stats.mean.toFixed(1)}  \u0303x=${stats.median.toFixed(1)}  \u03C3=${stats.std.toFixed(1)}`,
      showarrow: false,
      font: { size: 10, color: getThemeColors().tickColor, family: "'SF Mono', monospace" },
      xanchor: 'right' as const, yanchor: 'top' as const,
      bgcolor: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)',
      borderpad: 3,
    }] : [];

    return (
      <Plot
        data={traces}
        layout={makeLayout({
          barmode: 'overlay',
          xaxis: { ...makeLayout().xaxis, title: { text: getAxisLabel(column), font: { size: 11, color: '#888' } } },
          yaxis: { ...makeLayout().yaxis, title: { text: 'Count', font: { size: 11, color: '#888' } } },
          annotations: statsAnnotation,
        })}
        config={PLOTLY_CONFIG}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    );
  }, [molecules, selectedMoleculeIds, getNumeric, getMolId, isDark, getAxisLabel, histogramBins]);

  // ── Box / Violin data groups (for stats) ──
  const boxGroups = useMemo(() => {
    const zones = Array.from(new Set(molecules.map(getZone))).sort();
    return zones.map(zone => {
      const subset = molecules.filter(m => getZone(m) === zone);
      return {
        name: ZONE_LABELS[zone],
        data: subset.map(m => getNumeric(m, boxCol)).filter((v): v is number => v !== null),
      };
    }).filter(g => g.data.length > 0);
  }, [molecules, getZone, getNumeric, boxCol]);

  // ── Box / Violin Plot ──
  const renderBoxViolin = useCallback((column: string, mode: 'box' | 'violin', withStats: boolean) => {
    const zones = Array.from(new Set(molecules.map(getZone))).sort();
    const groupNames: string[] = [];
    const traces = zones.map(zone => {
      const subset = molecules.filter(m => getZone(m) === zone);
      const values = subset.map(m => getNumeric(m, column)).filter((v): v is number => v !== null);
      const label = ZONE_LABELS[zone];
      groupNames.push(label);

      if (mode === 'violin') {
        return {
          y: values,
          type: 'violin' as const,
          name: label,
          marker: { color: ZONE_COLORS[zone] },
          line: { color: ZONE_COLORS[zone] },
          fillcolor: ZONE_COLORS[zone] + '20',
          points: 'all' as const,
          jitter: 0.4,
          meanline: { visible: true },
          box: { visible: true, width: 0.1, fillcolor: ZONE_COLORS[zone] + '40', line: { color: ZONE_COLORS[zone] } },
          spanmode: 'hard' as const,
          hovertemplate: `${column}: %{y}<extra></extra>`,
        };
      }
      return {
        y: values,
        type: 'box' as const,
        name: label,
        marker: { color: ZONE_COLORS[zone], outliercolor: ZONE_COLORS[zone] + '80', size: 4 },
        line: { color: ZONE_COLORS[zone] },
        fillcolor: ZONE_COLORS[zone] + '20',
        boxpoints: 'outliers' as const,
        whiskerwidth: 0.5,
        jitter: 0.3,
        pointpos: 0,
        hovertemplate: `${column}: %{y}<extra></extra>`,
      };
    });

    // Build significance brackets if stats enabled
    let sigAnnotations: any[] = [];
    let sigShapes: any[] = [];
    if (withStats && groupNames.length >= 2) {
      const groups = zones.map(zone => {
        const subset = molecules.filter(m => getZone(m) === zone);
        return {
          name: ZONE_LABELS[zone],
          data: subset.map(m => getNumeric(m, column)).filter((v): v is number => v !== null),
        };
      }).filter(g => g.data.length >= 2);

      if (groups.length >= 3) {
        const anovaResult = oneWayAnova(groups);
        if (anovaResult?.significant) {
          const postHoc = tukeyHSD(groups);
          const result = buildSignificanceBrackets(postHoc, groupNames);
          if (result.length === 2) { sigAnnotations = result[0]; sigShapes = result[1]; }
        }
      } else if (groups.length === 2) {
        const postHoc = tukeyHSD(groups);
        const result = buildSignificanceBrackets(postHoc, groupNames);
        if (result.length === 2) { sigAnnotations = result[0]; sigShapes = result[1]; }
      }
    }

    return (
      <Plot
        data={traces}
        layout={makeLayout({
          yaxis: { ...makeLayout().yaxis, title: { text: getAxisLabel(column), font: { size: 11, color: '#888' } } },
          annotations: sigAnnotations,
          shapes: sigShapes,
          margin: withStats && sigAnnotations && sigAnnotations.length > 0 ? { t: 40, r: 16, b: 44, l: 54 } : undefined,
        })}
        config={PLOTLY_CONFIG}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    );
  }, [molecules, getZone, getNumeric, isDark, getAxisLabel]);

  // ── Bar Chart (count by category) ──
  const renderBarChart = useCallback((groupCol: string) => {
    // Group molecules
    const groups: Record<string, Molecule[]> = {};
    molecules.forEach(m => {
      let key: string;
      if (groupCol === 'zone') {
        const z = getZone(m);
        key = ZONE_LABELS[z] || `Zone ${z}`;
      } else {
        const val = getProp(m, groupCol);
        key = val != null ? String(val) : '(empty)';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });

    // Sort by count descending
    const sortedKeys = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);
    const counts = sortedKeys.map(k => groups[k].length);

    // Selected overlay
    const selectedCounts = sortedKeys.map(k =>
      groups[k].filter(m => selectedMoleculeIds.has(getMolId(m))).length
    );

    // Colours
    const barColors = groupCol === 'zone'
      ? sortedKeys.map(k => {
          const entry = Object.entries(ZONE_LABELS).find(([, v]) => v === k);
          return entry ? ZONE_COLORS[parseInt(entry[0])] : SPOTIFY_GREEN;
        })
      : sortedKeys.map((_, i) => {
          const opacity = 0.4 + 0.6 * (1 - i / Math.max(sortedKeys.length - 1, 1));
          const r = 29, g = 185, b = 84;
          return `rgba(${r},${g},${b},${opacity})`;
        });

    const traces: any[] = [{
      x: sortedKeys,
      y: counts,
      type: 'bar',
      marker: {
        color: barColors,
        line: { color: barColors, width: 1 },
      },
      text: counts.map(String),
      textposition: 'auto',
      textfont: { color: '#888', size: 10 },
      name: 'All',
      hovertemplate: '%{x}<br>Count: %{y}<extra></extra>',
    }];

    if (selectedMoleculeIds.size > 0 && selectedCounts.some(c => c > 0)) {
      traces.push({
        x: sortedKeys,
        y: selectedCounts,
        type: 'bar',
        marker: { color: barColors, line: { color: barColors, width: 1 } },
        text: selectedCounts.map(c => c > 0 ? String(c) : ''),
        textposition: 'auto',
        textfont: { color: '#fff', size: 10 },
        name: 'Selected',
        hovertemplate: '%{x}<br>Selected: %{y}<extra></extra>',
      });
    }

    return (
      <Plot
        data={traces}
        layout={makeLayout({
          barmode: 'overlay',
          xaxis: { ...makeLayout().xaxis, title: { text: getAxisLabel(groupCol), font: { size: 11, color: '#888' } }, tickangle: sortedKeys.length > 6 ? -45 : 0 },
          yaxis: { ...makeLayout().yaxis, title: { text: 'Count', font: { size: 11, color: '#888' } } },
          margin: { t: 8, r: 16, b: sortedKeys.length > 6 ? 80 : 44, l: 54 },
        })}
        config={PLOTLY_CONFIG}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    );
  }, [molecules, selectedMoleculeIds, getZone, getMolId, isDark, getAxisLabel]);

  // ── 3D Scatter ──
  const render3DScatter = useCallback((xKey: string, yKey: string, zKey: string) => {
    const validMols = molecules.filter(m =>
      getNumeric(m, xKey) !== null && getNumeric(m, yKey) !== null && getNumeric(m, zKey) !== null
    );
    const colourIsNumeric = colourBy !== 'none' && numericColumns.includes(colourBy);
    const colourCategories = colourIsNumeric || colourBy === 'zone' || colourBy === 'none'
      ? [] : Array.from(new Set(validMols.map(m => getCategory(m, colourBy))));
    const shapeCategories = shapeBy === 'none'
      ? [] : Array.from(new Set(validMols.map(m => getCategory(m, shapeBy))));
    const scatter3dSymbols = ['circle', 'square', 'diamond', 'cross', 'x', 'circle-open', 'square-open', 'diamond-open'];
    const sizes = sizeBy === 'none'
      ? validMols.map(() => 5)
      : numericRange(validMols.map(m => getNumeric(m, sizeBy)), 4, 12);

    const trace: any = {
      x: validMols.map(m => getNumeric(m, xKey)),
      y: validMols.map(m => getNumeric(m, yKey)),
      z: validMols.map(m => getNumeric(m, zKey)),
      text: validMols.map(m => {
        const details = [`<b>${getName(m)}</b>`, `${getAxisLabel(xKey)}: ${getProp(m, xKey) ?? '—'}`, `${getAxisLabel(yKey)}: ${getProp(m, yKey) ?? '—'}`, `${getAxisLabel(zKey)}: ${getProp(m, zKey) ?? '—'}`];
        if (colourBy === 'zone') details.push(ZONE_LABELS[getZone(m)]);
        return details.join('<br>');
      }),
      hoverinfo: 'text' as const,
      mode: 'markers' as const,
      type: 'scatter3d' as const,
      marker: {
        color: colourBy === 'none'
          ? validMols.map(() => '#0f9f8f')
          : colourIsNumeric
          ? validMols.map(m => getNumeric(m, colourBy))
          : colourBy === 'zone'
            ? validMols.map(m => ZONE_COLORS[getZone(m)])
            : validMols.map(m => SCATTER_CATEGORY_COLOURS[
                Math.max(0, colourCategories.indexOf(getCategory(m, colourBy))) % SCATTER_CATEGORY_COLOURS.length
              ]),
        colorscale: colourIsNumeric ? 'Viridis' : undefined,
        showscale: colourIsNumeric,
        colorbar: colourIsNumeric ? { title: { text: getAxisLabel(colourBy), font: { size: 10 } }, thickness: 12 } : undefined,
        symbol: shapeBy === 'none' ? 'circle' : validMols.map(m =>
          scatter3dSymbols[Math.max(0, shapeCategories.indexOf(getCategory(m, shapeBy))) % scatter3dSymbols.length]
        ),
        size: sizes.map((size, i) => selectedMoleculeIds.has(getMolId(validMols[i])) ? size + 3 : size),
        opacity: validMols.map(m => scatterPointOpacity(
          pointOpacity,
          selectedMoleculeIds.size > 0,
          selectedMoleculeIds.has(getMolId(m)),
        )),
        line: {
          color: validMols.map(m => selectedMoleculeIds.has(getMolId(m)) ? '#fff' : 'transparent'),
          width: 1,
        },
      },
    };

    return (
      <Plot
        data={[trace]}
        layout={makeLayout({
          margin: { t: 0, r: 0, b: 0, l: 0 },
          scene: {
            xaxis: { title: getAxisLabel(xKey), gridcolor: getThemeColors().gridColor, color: getThemeColors().tickColor },
            yaxis: { title: getAxisLabel(yKey), gridcolor: getThemeColors().gridColor, color: getThemeColors().tickColor },
            zaxis: { title: getAxisLabel(zKey), gridcolor: getThemeColors().gridColor, color: getThemeColors().tickColor },
            bgcolor: getThemeColors().plotBg,
          },
          dragmode: 'orbit',
        })}
        config={{ ...PLOTLY_CONFIG, scrollZoom: false }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
        onClick={(e: any) => {
          if (e?.points?.length) {
            const id = validMols[e.points[0].pointNumber] ? getMolId(validMols[e.points[0].pointNumber]) : null;
            if (id != null) {
              const next = new Set(selectedMoleculeIds);
              if (next.has(id)) next.delete(id); else next.add(id);
              handleSelect(next);
            }
          }
        }}
      />
    );
  }, [molecules, selectedMoleculeIds, getZone, getNumeric, getName, getMolId, getCategory, handleSelect,
    isDark, getAxisLabel, numericColumns, colourBy, sizeBy, shapeBy, pointOpacity]);

  // ── Parallel Coordinates ──
  const renderParallelCoordinates = useCallback(() => {
    const dimensions = numericColumns.map(col => {
      const values = molecules.map(m => getNumeric(m, col) ?? 0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      return {
        label: getAxisLabel(col),
        values,
        range: [min - (max - min) * 0.05, max + (max - min) * 0.05],
      };
    });

    const zoneValues = molecules.map(m => getZone(m));

    const trace = {
      type: 'parcoords' as const,
      line: {
        color: zoneValues,
        colorscale: [
          [0, ZONE_COLORS[1]],
          [0.25, ZONE_COLORS[2]],
          [0.5, ZONE_COLORS[3]],
          [0.75, ZONE_COLORS[4]],
          [1, ZONE_COLORS[5]],
        ],
        showscale: false,
        cmin: 1,
        cmax: 5,
      },
      dimensions,
    };

    return (
      <Plot
        data={[trace]}
        layout={makeLayout({
          margin: { t: 40, r: 20, b: 20, l: 20 },
        })}
        config={PLOTLY_CONFIG}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    );
  }, [molecules, numericColumns, getZone, getNumeric, isDark, getAxisLabel]);

  // ── Radar / Spider Chart ──
  const renderRadar = useCallback(() => {
    const radarCols = numericColumns.slice(0, 8);
    if (radarCols.length < 3) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: '13px' }}>
          Need at least 3 numeric columns for radar chart.
        </div>
      );
    }

    // Min-max for normalisation
    const mm = radarCols.map(col => columnMinMax[col] || { min: 0, max: 1 });
    const normalise = (val: number, idx: number) => {
      const range = mm[idx].max - mm[idx].min;
      return range === 0 ? 0.5 : (val - mm[idx].min) / range;
    };

    const traces: any[] = [];
    const selectedMols = molecules.filter(m => selectedMoleculeIds.has(getMolId(m)));
    const thetaLabels = [...radarCols.map(c => getAxisLabel(c)), getAxisLabel(radarCols[0])]; // close the polygon

    if (selectedMols.length === 0) {
      // Show all compounds as faint grey
      molecules.forEach(mol => {
        const rValues = radarCols.map((col, i) => {
          const v = getNumeric(mol, col);
          return v !== null ? normalise(v, i) : 0;
        });
        rValues.push(rValues[0]); // close polygon
        traces.push({
          type: 'scatterpolar' as const,
          r: rValues,
          theta: thetaLabels,
          fill: 'toself' as const,
          fillcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          line: { color: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', width: 1 },
          mode: 'lines' as const,
          name: getName(mol),
          hoverinfo: 'skip' as const,
          showlegend: false,
        });
      });
    } else {
      // Show selected compounds with coloured polygons
      const opacity = selectedMols.length <= 5 ? 0.7 : 0.4;
      selectedMols.forEach(mol => {
        const zone = getZone(mol);
        const color = ZONE_COLORS[zone];
        const rValues = radarCols.map((col, i) => {
          const v = getNumeric(mol, col);
          return v !== null ? normalise(v, i) : 0;
        });
        rValues.push(rValues[0]);

        // Build hover text with actual values
        const hoverParts = radarCols.map(col => {
          const v = getNumeric(mol, col);
          return `${col}: ${v !== null ? v.toFixed(2) : 'N/A'}`;
        });

        traces.push({
          type: 'scatterpolar' as const,
          r: rValues,
          theta: thetaLabels,
          fill: 'toself' as const,
          fillcolor: color + Math.round(opacity * 255 * 0.3).toString(16).padStart(2, '0'),
          line: { color, width: 2 },
          mode: 'lines+markers' as const,
          marker: { size: 4, color },
          name: getName(mol),
          text: thetaLabels.map((col, i) => {
            if (i === thetaLabels.length - 1) return hoverParts[0];
            return hoverParts[i];
          }),
          hoverinfo: 'text+name' as const,
          showlegend: true,
        });
      });
    }

    // Drug-like reference polygon
    const refValues = radarCols.map((col, i) => {
      const refVal = DRUG_LIKE_REFERENCE[col];
      if (refVal === undefined) return null;
      return normalise(refVal, i);
    });
    const hasRef = refValues.some(v => v !== null);
    if (hasRef) {
      const rRef = refValues.map(v => v !== null ? v : 0);
      rRef.push(rRef[0]);
      traces.push({
        type: 'scatterpolar' as const,
        r: rRef,
        theta: thetaLabels,
        fill: 'none' as const,
        line: { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)', width: 1.5, dash: 'dash' },
        mode: 'lines' as const,
        name: 'Drug-like ref',
        hoverinfo: 'name' as const,
        showlegend: true,
      });
    }

    return (
      <div style={{ height: '100%', position: 'relative' }}>
        {selectedMols.length === 0 && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 2, color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center',
            pointerEvents: 'none', background: 'var(--card-bg)', padding: '8px 16px',
            borderRadius: '6px', border: '1px solid var(--card-border)',
          }}>
            Select compounds to compare profiles
          </div>
        )}
        <Plot
          data={traces}
          layout={(() => {
            const t = getThemeColors();
            return {
              paper_bgcolor: 'rgba(0,0,0,0)',
              font: { family: "'Inter', -apple-system, sans-serif", color: t.fontColor, size: 11 },
              margin: { t: 30, r: 40, b: 30, l: 40 },
              polar: {
                bgcolor: t.plotBg,
                radialaxis: {
                  visible: true, range: [0, 1.1],
                  gridcolor: t.gridColor,
                  tickfont: { size: 9, color: t.tickColor },
                  tickvals: [0, 0.25, 0.5, 0.75, 1],
                },
                angularaxis: {
                  gridcolor: t.zeroLineColor,
                  tickfont: { size: 10, color: t.fontColor },
                  linecolor: t.zeroLineColor,
                },
              },
              showlegend: selectedMols.length > 0 || hasRef,
              legend: {
                font: { size: 10, color: t.fontColor },
                bgcolor: 'rgba(0,0,0,0)',
                x: 1.05, y: 1,
              },
              autosize: true,
              hoverlabel: {
                bgcolor: t.hoverBg, bordercolor: t.hoverBorder,
                font: { family: "'Inter', sans-serif", color: t.hoverFont, size: 12 },
              },
            };
          })()}
          config={PLOTLY_CONFIG}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
        />
      </div>
    );
  }, [molecules, numericColumns, selectedMoleculeIds, columnMinMax, getZone, getNumeric, getName, getMolId, isDark, getAxisLabel]);

  // ── Correlation Heatmap ──
  const renderCorrelationHeatmap = useCallback(() => {
    const cols = numericColumns;
    const n = cols.length;

    // Extract paired values for each column
    const colValues: number[][] = cols.map(col =>
      molecules.map(m => getNumeric(m, col) ?? NaN)
    );

    // Build correlation matrix
    const zMatrix: number[][] = [];
    const annotations: any[] = [];

    for (let i = 0; i < n; i++) {
      const row: number[] = [];
      for (let j = 0; j < n; j++) {
        // Get paired non-null values
        const xs: number[] = [], ys: number[] = [];
        for (let k = 0; k < molecules.length; k++) {
          const vx = colValues[i][k], vy = colValues[j][k];
          if (!isNaN(vx) && !isNaN(vy)) { xs.push(vx); ys.push(vy); }
        }
        const r = pearsonR(xs, ys);
        row.push(r);
        annotations.push({
          x: cols[j],
          y: cols[i],
          text: r.toFixed(2),
          font: { color: Math.abs(r) > 0.5 ? '#fff' : getThemeColors().tickColor, size: 10 },
          showarrow: false,
        });
      }
      zMatrix.push(row);
    }

    return (
      <Plot
        data={[{
          z: zMatrix,
          x: cols.map(c => getAxisLabel(c)),
          y: cols.map(c => getAxisLabel(c)),
          type: 'heatmap' as const,
          colorscale: [
            [0, '#ef4444'],
            [0.25, '#f97316'],
            [0.5, isDark ? '#1e1e1e' : '#f3f4f6'],
            [0.75, '#22c55e'],
            [1, '#1db954'],
          ],
          zmin: -1,
          zmax: 1,
          hovertemplate: '%{y} vs %{x}<br>r = %{z:.3f}<extra></extra>',
          showscale: true,
          colorbar: {
            tickfont: { color: getThemeColors().tickColor, size: 10 },
            title: { text: 'r', font: { color: getThemeColors().tickColor, size: 11 } },
            outlinecolor: isDark ? '#333' : 'var(--card-border)',
            outlinewidth: 1,
          },
        }]}
        layout={makeLayout({
          margin: { t: 30, r: 80, b: 100, l: 100 },
          xaxis: {
            ...makeLayout().xaxis,
            tickangle: -45,
            side: 'bottom' as const,
          },
          yaxis: {
            ...makeLayout().yaxis,
            autorange: 'reversed' as const,
          },
          annotations,
          dragmode: false,
        })}
        config={{ ...PLOTLY_CONFIG, scrollZoom: false }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    );
  }, [molecules, numericColumns, getNumeric, isDark, getAxisLabel]);

  // ── Mini Compound Table (right panel) ──
  const renderCompoundTable = useCallback(() => {
    const displayCols = numericColumns.slice(0, 5);
    const groupedRows = partitionRowsBySelection(molecules, selectedMoleculeIds, getMolId);
    const orderedRows = selectedMoleculeIds.size > 0
      ? [...groupedRows.selected, ...groupedRows.unselected]
      : groupedRows.unselected;
    const tableColumnCount = 5 + displayCols.length;
    const groupHeadingStyle: React.CSSProperties = {
      padding: '5px 8px',
      background: isDark ? 'rgba(45, 212, 191, 0.16)' : '#dff7f3',
      color: isDark ? '#99f6e4' : '#115e59',
      borderTop: '1px solid var(--card-border)',
      borderBottom: '1px solid var(--card-border)',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    };

    return (
      <div style={{ height: '100%', overflow: 'auto', fontSize: '11px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: 'var(--card-bg)', zIndex: 1 }}>
              <th style={{ ...thStyle, width: 28 }}>
                <input
                  aria-label="Select all compounds"
                  type="checkbox"
                  checked={molecules.length > 0 && selectedMoleculeIds.size === molecules.length}
                  onChange={e => handleSelect(e.target.checked ? new Set(molecules.map(getMolId)) : new Set())}
                />
              </th>
              <th style={thStyle}>Structure</th>
              <th style={thStyle}>Zone</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Fit</th>
              {displayCols.map(col => <th key={col} style={thStyle}>{getAxisLabel(col)}</th>)}
            </tr>
          </thead>
          <tbody>
            {selectedMoleculeIds.size > 0 && groupedRows.selected.length > 0 && (
              <tr>
                <td colSpan={tableColumnCount} style={groupHeadingStyle}>
                  Selected compounds · {groupedRows.selected.length}
                </td>
              </tr>
            )}
            {orderedRows.map((mol, rowIndex) => {
              const molId = getMolId(mol);
              const isSelected = selectedMoleculeIds.has(molId);
              const zone = getZone(mol);
              return (
                <React.Fragment key={molId}>
                  {selectedMoleculeIds.size > 0
                    && rowIndex === groupedRows.selected.length
                    && groupedRows.unselected.length > 0 && (
                    <tr>
                      <td colSpan={tableColumnCount} style={{
                        ...groupHeadingStyle,
                        background: 'var(--card-bg)',
                        color: 'var(--text-muted)',
                      }}>
                        Other compounds · {groupedRows.unselected.length}
                      </td>
                    </tr>
                  )}
                  <tr
                    onClick={() => {
                      const newSet = new Set(selectedMoleculeIds);
                      if (newSet.has(molId)) newSet.delete(molId);
                      else newSet.add(molId);
                      handleSelect(newSet);
                    }}
                    onDoubleClick={() => onMoleculeClick?.(molId)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected
                        ? (isDark ? 'rgba(45, 212, 191, 0.14)' : '#ecfdf5')
                        : 'transparent',
                      opacity: 1,
                      transition: 'all 0.12s ease',
                      borderBottom: '1px solid var(--divider)',
                    }}
                  >
                  <td style={{ ...tdStyle, width: 28 }} onClick={e => e.stopPropagation()}>
                    <input
                      aria-label={`Select ${getName(mol)}`}
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => {
                        const next = new Set(selectedMoleculeIds);
                        if (e.target.checked) next.add(molId); else next.delete(molId);
                        handleSelect(next);
                      }}
                    />
                  </td>
                  <td style={{ ...tdStyle, width: 48 }}>
                    <StructureImage smiles={mol.smiles} moleculeId={molId} size={42} lazy />
                  </td>
                  <td style={{ ...tdStyle, width: '28px' }}>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: ZONE_COLORS[zone],
                      boxShadow: `0 0 6px ${ZONE_COLORS[zone]}40`,
                    }} />
                  </td>
                  <td style={{
                    ...tdStyle,
                    color: isSelected ? SPOTIFY_GREEN : 'var(--text-primary)',
                    fontWeight: isSelected ? 600 : 400,
                    maxWidth: '140px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    <button
                      type="button"
                      title="Open compound details"
                      onClick={event => {
                        event.stopPropagation();
                        onMoleculeClick?.(molId);
                      }}
                      style={{
                        border: 0, padding: 0, background: 'transparent', cursor: 'pointer',
                        color: 'inherit', font: 'inherit', fontWeight: 'inherit',
                        textDecoration: 'underline', textDecorationColor: 'var(--card-border)',
                        textUnderlineOffset: 2, maxWidth: '100%', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >{getName(mol)}</button>
                  </td>
                  <td style={{ ...tdStyle, width: 38 }} onClick={e => e.stopPropagation()}>
                    <input
                      aria-label={`Include ${getName(mol)} in regression`}
                      type="checkbox"
                      checked={!excludedRegressionIds.has(molId)}
                      onChange={e => setExcludedRegressionIds(previous => {
                        const next = new Set(previous);
                        if (e.target.checked) next.delete(molId); else next.add(molId);
                        return next;
                      })}
                    />
                  </td>
                  {displayCols.map(col => (
                    <td key={col} style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                      {getProp(mol, col) ?? '\u2014'}
                    </td>
                  ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }, [molecules, numericColumns, selectedMoleculeIds, excludedRegressionIds, getZone, getName, getMolId, handleSelect, onMoleculeClick, getAxisLabel, isDark]);

  // ── Zone Legend ──
  const zoneCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    molecules.forEach(m => {
      const z = getZone(m);
      counts[z] = (counts[z] || 0) + 1;
    });
    return counts;
  }, [molecules, getZone]);

  if (numericColumns.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '400px', color: 'var(--text-muted)', fontSize: '14px',
      }}>
        No numeric columns detected. Upload data with numeric properties to use Analyse view.
      </div>
    );
  }

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--content-bg)',
      color: 'var(--text-primary)',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* ── Toolbar ── */}
      <div style={{
        padding: '8px 16px',
        borderBottom: '1px solid var(--card-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--hover-bg)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          {colourBy === 'none' && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Points use one neutral colour. Choose a data column below to colour by value.</span>
          )}
          {colourBy === 'zone' && Object.entries(ZONE_COLORS).map(([zone, color]) => (
            <div key={zone} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '7px', height: '7px', borderRadius: '50%',
                background: color, boxShadow: `0 0 5px ${color}40`,
              }} />
              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                {ZONE_LABELS[parseInt(zone)]} ({zoneCounts[parseInt(zone)] || 0})
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {selectedMoleculeIds.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'rgba(29, 185, 84, 0.1)',
              border: '1px solid rgba(29, 185, 84, 0.3)',
              borderRadius: '20px', padding: '3px 12px',
            }}>
              <span style={{ fontSize: '11px', color: SPOTIFY_GREEN, fontWeight: 600 }}>
                {selectedMoleculeIds.size} selected
              </span>
              <span
                onClick={clearSelection}
                style={{ fontSize: '13px', color: SPOTIFY_GREEN, cursor: 'pointer', lineHeight: 1, marginLeft: '2px' }}
              >&times;</span>
            </div>
          )}

          {selectedMoleculeIds.size > 0 && (
            <>
              <button
                onClick={() => setExcludedRegressionIds(previous => {
                  const next = new Set(previous);
                  selectedMoleculeIds.forEach(id => next.add(id));
                  return next;
                })}
                style={toolbarButtonStyle}
              >Withhold selected from fit</button>
              <button
                onClick={() => setExcludedRegressionIds(previous => {
                  const next = new Set(previous);
                  selectedMoleculeIds.forEach(id => next.delete(id));
                  return next;
                })}
                style={toolbarButtonStyle}
              >Restore selected to fit</button>
            </>
          )}
          {excludedRegressionIds.size > 0 && (
            <button onClick={() => setExcludedRegressionIds(new Set())} style={toolbarButtonStyle}>
              Restore all ({excludedRegressionIds.size})
            </button>
          )}
          <ExportButton
            dataType="molecules"
            totalCount={molecules.length}
            selectedCount={selectedMoleculeIds.size}
            moleculeIds={Array.from(selectedMoleculeIds)}
            fileId={fileId}
            compact
          />

          <div style={{
            display: 'flex', background: 'var(--card-bg)', borderRadius: '6px',
            border: '1px solid var(--card-border)', overflow: 'hidden',
          }}>
            {LAYOUT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setLayoutMode(opt.key)}
                title={opt.label}
                style={{
                  padding: '5px 10px',
                  border: 'none',
                  background: layoutMode === opt.key ? 'rgba(29, 185, 84, 0.15)' : 'transparent',
                  color: layoutMode === opt.key ? SPOTIFY_GREEN : 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  borderRight: '1px solid var(--card-border)',
                  transition: 'all 0.15s ease',
                }}
              >
                {opt.icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CDD-style visual encodings and axis controls. These settings are shared by
          the linked scatter plots so brushing, the table and every plot stay in sync. */}
      {(layoutMode === 'scatter-table' || layoutMode === '2x2' || layoutMode === '3d') && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '6px 16px', borderBottom: '1px solid var(--card-border)',
          background: 'var(--card-bg)', flexShrink: 0,
        }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Plot settings
          </span>
          <AxisDropdown label="Colour" value={colourBy} onChange={setColourBy}
            options={['none', 'zone', ...numericColumns, ...categoricalColumns.filter(c => c !== 'zone' && !numericColumns.includes(c))]}
            getLabel={c => c === 'none' ? 'Fixed colour' : c === 'zone' ? 'Patent similarity' : getAxisLabel(c)} />
          <AxisDropdown label="Size" value={sizeBy} onChange={setSizeBy}
            options={['none', ...numericColumns]} getLabel={c => c === 'none' ? 'Fixed' : getAxisLabel(c)} />
          <AxisDropdown label="Shape" value={shapeBy} onChange={setShapeBy}
            options={['none', ...allDataColumns]} getLabel={c => c === 'none' ? 'Circle' : getAxisLabel(c)} />

          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: 'var(--text-muted)' }}>
            Opacity
            <input type="range" min="0.15" max="1" step="0.05" value={pointOpacity}
              onChange={e => setPointOpacity(Number(e.target.value))} style={{ width: 72 }} />
            <span style={{ width: 28, fontVariantNumeric: 'tabular-nums' }}>{Math.round(pointOpacity * 100)}%</span>
          </label>

          {layoutMode !== '3d' && <>
            <MiniToggle options={[{ key: 'linear', label: 'X linear' }, { key: 'log', label: 'X log' }]}
              value={scatterXScale} onChange={v => setScatterXScale(v as 'linear' | 'log')} />
            <MiniToggle options={[{ key: 'linear', label: 'Y linear' }, { key: 'log', label: 'Y log' }]}
              value={scatterYScale} onChange={v => setScatterYScale(v as 'linear' | 'log')} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-muted)' }}>
              X range
              <input aria-label="X minimum" placeholder="min" value={scatterXMin} onChange={e => setScatterXMin(e.target.value)} style={rangeInputStyle} />
              <input aria-label="X maximum" placeholder="max" value={scatterXMax} onChange={e => setScatterXMax(e.target.value)} style={rangeInputStyle} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-muted)' }}>
              Y range
              <input aria-label="Y minimum" placeholder="min" value={scatterYMin} onChange={e => setScatterYMin(e.target.value)} style={rangeInputStyle} />
              <input aria-label="Y maximum" placeholder="max" value={scatterYMax} onChange={e => setScatterYMax(e.target.value)} style={rangeInputStyle} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showTrendline} onChange={e => setShowTrendline(e.target.checked)} />
              Linear fit
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showIdentityLine} onChange={e => setShowIdentityLine(e.target.checked)} />
              y = x
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showMarginalHistograms} onChange={e => setShowMarginalHistograms(e.target.checked)} />
              Marginal X/Y histograms
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeQualifiedInFit} onChange={e => setIncludeQualifiedInFit(e.target.checked)} />
              Include qualified values in fit
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showThresholds} onChange={e => {
                const enabled = e.target.checked;
                setShowThresholds(enabled);
                if (enabled && xThreshold.trim() === '') {
                  const range = columnMinMax[scatterX];
                  if (range) setXThreshold(String(scatterXScale === 'log' && range.min > 0 ? Math.sqrt(range.min * range.max) : (range.min + range.max) / 2));
                }
                if (enabled && yThreshold.trim() === '') {
                  const range = columnMinMax[scatterY];
                  if (range) setYThreshold(String(scatterYScale === 'log' && range.min > 0 ? Math.sqrt(range.min * range.max) : (range.min + range.max) / 2));
                }
              }} />
              Draggable thresholds
            </label>
            {showThresholds && <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-muted)' }}>
                X <input aria-label="X threshold" placeholder="value" value={xThreshold} onChange={e => setXThreshold(e.target.value)} style={rangeInputStyle} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-muted)' }}>
                Y <input aria-label="Y threshold" placeholder="value" value={yThreshold} onChange={e => setYThreshold(e.target.value)} style={rangeInputStyle} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={showQuadrants} onChange={e => setShowQuadrants(e.target.checked)} />
                Quadrants
              </label>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Drag either dotted line on the plot or type a value.</span>
            </>}
          </>}

          {layoutMode === '2x2' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)' }}>
              Histogram bins
              <input type="number" min="2" max="100" value={histogramBins}
                onChange={e => setHistogramBins(Math.min(100, Math.max(2, Number(e.target.value) || 2)))}
                style={{ ...rangeInputStyle, width: 48 }} />
            </label>
          )}

          <button onClick={() => {
            setColourBy('none'); setSizeBy('none'); setShapeBy('none'); setPointOpacity(DEFAULT_SCATTER_POINT_OPACITY);
            setShowTrendline(false); setScatterXScale('linear'); setScatterYScale('linear');
            setShowIdentityLine(false); setShowMarginalHistograms(true); setIncludeQualifiedInFit(false); setExcludedRegressionIds(new Set());
            setShowThresholds(false); setShowQuadrants(false); setXThreshold(''); setYThreshold('');
            setScatterXMin(''); setScatterXMax(''); setScatterYMin(''); setScatterYMax(''); setHistogramBins(12);
          }} style={{
            border: '1px solid var(--card-border)', borderRadius: 4, padding: '3px 7px',
            background: 'var(--hover-bg)', color: 'var(--text-muted)', fontSize: 10, cursor: 'pointer',
          }}>Reset plot</button>

          <span
            title="Analyse plots imported values as supplied; it does not calculate pH-dependent LogD."
            style={{ fontSize: 10, color: 'var(--text-muted)' }}
          >Imported values only</span>

          <input
            aria-label="Saved chart name"
            list="saved-chart-names"
            placeholder="Chart name"
            value={savedChartName}
            onChange={e => setSavedChartName(e.target.value)}
            style={{ ...rangeInputStyle, width: 112 }}
          />
          <datalist id="saved-chart-names">
            {savedCharts.map(chart => <option key={chart.name} value={chart.name} />)}
          </datalist>
          <button onClick={saveCurrentChart} style={toolbarButtonStyle}>Save chart</button>
          <button
            onClick={() => {
              const chart = savedCharts.find(item => item.name === savedChartName);
              if (chart) applySavedChart(chart.config);
            }}
            disabled={!savedCharts.some(item => item.name === savedChartName)}
            style={toolbarButtonStyle}
          >Load</button>
          <button onClick={deleteSavedChart} disabled={!savedCharts.some(item => item.name === savedChartName)} style={toolbarButtonStyle}>
            Delete saved
          </button>
        </div>
      )}

      {/* ── Chart Area ── */}
      <div style={{ flex: 1, padding: '10px', minHeight: 0 }}>

        {layoutMode === 'scatter-table' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', height: '100%' }}>
            <ChartPanel title="Scatter Plot" icon={'\u25c9'} controls={<>
              <AxisDropdown label="X" value={scatterX} onChange={setScatterX} options={numericColumns} getLabel={getAxisLabel} />
              <AxisDropdown label="Y" value={scatterY} onChange={setScatterY} options={numericColumns} getLabel={getAxisLabel} />
            </>}>
              {renderScatter(scatterX, scatterY)}
            </ChartPanel>
            <ChartPanel title="Compounds" icon={'\u229f'}>
              {renderCompoundTable()}
            </ChartPanel>
          </div>
        )}

        {layoutMode === '2x2' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '10px', height: '100%' }}>
            <ChartPanel title="Scatter Plot" icon={'\u25c9'} controls={<>
              <AxisDropdown label="X" value={scatterX} onChange={setScatterX} options={numericColumns} getLabel={getAxisLabel} />
              <AxisDropdown label="Y" value={scatterY} onChange={setScatterY} options={numericColumns} getLabel={getAxisLabel} />
            </>}>
              {renderScatter(scatterX, scatterY)}
            </ChartPanel>

            {/* Distribution / Bar toggle panel */}
            <ChartPanel
              title={distMode === 'histogram' ? 'Distribution' : 'Count by Category'}
              icon={distMode === 'histogram' ? '\u25a5' : '\u2591'}
              controls={<>
                <MiniToggle
                  options={[{ key: 'histogram', label: 'Dist' }, { key: 'bar', label: 'Count' }]}
                  value={distMode}
                  onChange={v => setDistMode(v as 'histogram' | 'bar')}
                />
                {distMode === 'histogram'
                  ? <AxisDropdown label="Col" value={histCol} onChange={setHistCol} options={numericColumns} getLabel={getAxisLabel} />
                  : <AxisDropdown label="By" value={barGroupCol} onChange={setBarGroupCol} options={categoricalColumns} />
                }
              </>}
            >
              {distMode === 'histogram' ? renderHistogram(histCol) : renderBarChart(barGroupCol)}
            </ChartPanel>

            {/* Box / Violin toggle panel */}
            <ChartPanel
              title={boxMode === 'box' ? 'Box Plot by Zone' : 'Violin Plot by Zone'}
              icon={'\u229e'}
              controls={<>
                <MiniToggle
                  options={[{ key: 'violin', label: 'Violin' }, { key: 'box', label: 'Box' }]}
                  value={boxMode}
                  onChange={v => setBoxMode(v as 'box' | 'violin')}
                />
                <MiniToggle
                  options={[{ key: 'off', label: '\u2014' }, { key: 'on', label: 'Stats' }]}
                  value={showStats ? 'on' : 'off'}
                  onChange={v => setShowStats(v === 'on')}
                />
                <AxisDropdown label="Val" value={boxCol} onChange={setBoxCol} options={numericColumns} getLabel={getAxisLabel} />
              </>}
            >
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ flex: showStats ? '0 0 55%' : '1 1 100%', minHeight: 0 }}>
                  {renderBoxViolin(boxCol, boxMode, showStats)}
                </div>
                {showStats && (
                  <StatsPanel groups={boxGroups} column={boxCol} />
                )}
              </div>
            </ChartPanel>

            <ChartPanel title="Compounds" icon={'\u229f'}>
              {renderCompoundTable()}
            </ChartPanel>
          </div>
        )}

        {layoutMode === '3d' && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', height: '100%' }}>
            <ChartPanel title="3D Chemical Space" icon={'\u25c8'} controls={<>
              <AxisDropdown label="X" value={scatter3dX} onChange={setScatter3dX} options={numericColumns} getLabel={getAxisLabel} />
              <AxisDropdown label="Y" value={scatter3dY} onChange={setScatter3dY} options={numericColumns} getLabel={getAxisLabel} />
              <AxisDropdown label="Z" value={scatter3dZ} onChange={setScatter3dZ} options={numericColumns} getLabel={getAxisLabel} />
            </>}>
              {render3DScatter(scatter3dX, scatter3dY, scatter3dZ)}
            </ChartPanel>
            <ChartPanel title="Compounds" icon={'\u229f'}>
              {renderCompoundTable()}
            </ChartPanel>
          </div>
        )}

        {layoutMode === 'parcoords' && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', height: '100%' }}>
            <ChartPanel title="Parallel Coordinates" icon={'\u2261'}>
              {renderParallelCoordinates()}
            </ChartPanel>
            <ChartPanel title="Compounds" icon={'\u229f'}>
              {renderCompoundTable()}
            </ChartPanel>
          </div>
        )}

        {layoutMode === 'radar' && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', height: '100%' }}>
            <ChartPanel title="Radar Profile" icon={'\u25ce'}>
              {renderRadar()}
            </ChartPanel>
            <ChartPanel title="Compounds" icon={'\u229f'}>
              {renderCompoundTable()}
            </ChartPanel>
          </div>
        )}

        {layoutMode === 'correlation' && (
          <div style={{ height: '100%' }}>
            <ChartPanel title="Correlation Matrix" icon={'\u25a6'} style={{ height: '100%' }}>
              {renderCorrelationHeatmap()}
            </ChartPanel>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Table styles ──
const thStyle: React.CSSProperties = {
  padding: '7px 6px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600,
  fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em',
  borderBottom: '1px solid var(--card-border)',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 6px',
};

const rangeInputStyle: React.CSSProperties = {
  width: 58,
  padding: '2px 4px',
  borderRadius: 4,
  border: '1px solid var(--card-border)',
  background: 'var(--content-bg)',
  color: 'var(--text-primary)',
  fontSize: 10,
};

const toolbarButtonStyle: React.CSSProperties = {
  border: '1px solid var(--card-border)',
  borderRadius: 4,
  padding: '3px 7px',
  background: 'var(--hover-bg)',
  color: 'var(--text-secondary)',
  fontSize: 10,
  cursor: 'pointer',
};

export default AnalyseView;
